import { Button, MenuItem } from "@mui/material";

import { IRules, IRulesElegibility, IElegibilityPlans, IElegibilityRules, CoverageTypes, CoverageGeographicTypes, RemissionPeriodTypes, ElegibleType } from "../../Setup.types";
import { ButtonDelete, Container, Row, SelectForm, Subtitle, TextField, Title } from "./Rules.styled";
import { formatCurrency, maskPercentage } from "./Rules.utils";
import { useQuery } from "@tanstack/react-query";
import api from "services/api";
import { MultiSelectWithSelectAll } from "v2/components/ui";

interface IProps {
  rules: IRules;
  handleChange: (payload: Partial<IRules>) => void;
}

export function RulesTabElegibility({ rules, handleChange }: IProps) {
  const { plans, elegibilityRules } = rules.rulesElegibility;

  const { data, isFetching, isFetched } = useQuery({
    queryKey: ["organization-roles", rules.rulesRegister.buyerFederalTaxId],
    queryFn: () => api.get(`/v2//operation/organization-roles/${rules.rulesRegister.buyerFederalTaxId}`),
    select: (data) => data?.data?.data as string[],
  });

  if (!isFetched && isFetching) return <div>Loading...</div>;

  /** Opções possíveis da coluna de Cargo. */
  const roleOptions = data ?? [];
  /** Opções possíveis para coluna de Documentos. */
  const documentOptions = ["Termo de Ciencia Beneficiario", "Termo de Recisão Contrato de Trabalho (TRCT)", "União Estável", "RG/CPF", "Certidão Nascimento", "Caderneta da Criança", "Carta Assinada", "Ficha de Registro", "Comprovante Vínculo Empregatício", "Comprovaten Matrícula Faculdade"];

  /** Função responsável especificamente por receber os valores alterados e formatar corretamente para handleChange. */
  function handleChangeElegibility(payload: Partial<IRulesElegibility>) {
    const updatedElegibilityFields = { ...rules.rulesElegibility, ...payload };

    handleChange({ ...rules, rulesElegibility: updatedElegibilityFields });
  }

  /** Função responsável por adicionar campos à array de planos individualmente. */
  function handleChangePlans(payload: Partial<IElegibilityPlans>, index: number) {
    const updatedPayload = plans.map((p, i) => i === index ? { ...p, ...payload } : p);

    handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, plans: updatedPayload }});
  }

  /** Função responsável por adicionar novo campo vazio à array de planos. */
  function handleAddPlan() {
    const newPlan = { planName: undefined, planCode: undefined, planValue: undefined, allowedRoles: [] };

    return handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, plans: [...plans, newPlan] }});
  }

  /** Função responsável por remover campo da array de planos. */
  function handleRemovePlan(index: number) {
    const updatedPayload = plans.filter((_p, i) => i !== index);

    return handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, plans: updatedPayload }});
  }

  /** Função responsável por adicionar campos à array de regras de elegibilidade individualmente. */
  function handleChangeRules(payload: Partial<IElegibilityRules>, index: number) {
    const updatedPayload = elegibilityRules.map((r, i) => i === index ? { ...r, ...payload } : r);

    handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, elegibilityRules: updatedPayload }});
  }

  /** Função responsável por remover campo da array de regras de elegibilidade. */
  function handleRemoveRule(index: number) {
    const updatedPayload = elegibilityRules.filter((_r, i) => i !== index);

    return handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, elegibilityRules: updatedPayload }});
  }

  /** Função responsável por adicionar novo campo vazio à array de planos. */
  function handleAddRule() {
    const newRule = { elegible: undefined, rule: undefined };

    return handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, elegibilityRules: [...elegibilityRules, newRule] }});
  }

  return (
    <Container>
      <Title>Planos</Title>
      <Row>
        <TextField
          label="Co-participação"
          value={maskPercentage(rules.rulesElegibility.coParticipation)}
          onChange={(e) => handleChangeElegibility({ coParticipation: e.target.value })}
        />
        <SelectForm
          label="Tipo de cobertura"
          value={rules.rulesElegibility.coverageType}
          onChange={(e) => handleChangeElegibility({ coverageType: e.target.value as CoverageTypes })}
        >
          <MenuItem value={"AMBULATORIAL"}>Ambulatorial</MenuItem>
          <MenuItem value={"AMBULATORIAL_HOSPITALAR"}>Ambulatorial + Hospitalar</MenuItem>
          <MenuItem value={"AMBULATORIAL_HOSPITALAR_OBSTETRA"}>Ambulatorial + Hospitalar + Obstetra</MenuItem>
          <MenuItem value={"HOSPITALAR"}>Hospitalar</MenuItem>
        </SelectForm>
        <SelectForm
          label="Abrangência geográfica"
          value={rules.rulesElegibility.coverageGeographic}
          onChange={(e) => handleChangeElegibility({ coverageGeographic: e.target.value as CoverageGeographicTypes })}
        >
          <MenuItem value={"GRUPO_DE_MUNICIPIOS"}>Grupo de municípios</MenuItem>
          <MenuItem value={"NACIONAL"}>Nacional</MenuItem>
          <MenuItem value={"REGIONAL"}>Regional</MenuItem>
        </SelectForm>
      </Row>
      <Title>Elegibilidade</Title>
      <Row>
        <SelectForm
          label="Contribuição"
          value={rules.rulesElegibility.contribution === true ? "SIM" : rules.rulesElegibility.contribution === false ? "NAO" : undefined}
          onChange={(e) => handleChangeElegibility({ contribution: e.target.value === "SIM" ? true : false })}
        >
          <MenuItem value={"SIM"}>Sim</MenuItem>
          <MenuItem value={"NAO"}>Nao</MenuItem>
        </SelectForm>
        <TextField
          label="Contribuição titular"
          value={maskPercentage(rules.rulesElegibility.contributionEmployee)}
          onChange={(e) => handleChangeElegibility({ contributionEmployee: e.target.value })}
        />
        <TextField
          label="Contribuição dependente"
          value={maskPercentage(rules.rulesElegibility.contributionDependent)}
          onChange={(e) => handleChangeElegibility({ contributionDependent: e.target.value })}
        />
      </Row>
      <Row>
        <TextField
          label="Limite técnico"
          value={maskPercentage(rules.rulesElegibility.limit)}
          onChange={(e) => handleChangeElegibility({ limit: e.target.value })}
        />
        <SelectForm
          label="Período remissão"
          value={rules.rulesElegibility.remissionPeriod}
          onChange={(e) => handleChangeElegibility({ remissionPeriod: e.target.value as RemissionPeriodTypes })}
        >
          <MenuItem value={"12_MONTHS"}>12 Meses</MenuItem>
        </SelectForm>
        <TextField
          label="Valor taxa implantação"
          disabled
          value={rules.rulesElegibility.implantationCost}
          onChange={(e) => handleChangeElegibility({ implantationCost: e.target.value })}
        />
      </Row>
      <Title>Características do benefício</Title>
      <Row>
        <Subtitle>Planos</Subtitle>
        <Subtitle>Código</Subtitle>
        <Subtitle>Valor</Subtitle>
        <Subtitle>Possíveis cargos para o produto</Subtitle>
      </Row>
      {plans.map((plan, index) => (
        <Row style={{ alignItems: "center"}}>
          <TextField
            label="Plano"
            value={plan.planName}
            onChange={(e) => handleChangePlans({ planName: e.target.value }, index)}
          />
          <TextField
            label="Código"
            value={plan.planCode}
            onChange={(e) => handleChangePlans({ planCode: e.target.value }, index)}
          />
          <TextField
            label="Valor (R$)"
            value={formatCurrency(plan.planValue)}
            onChange={(e) => handleChangePlans({ planValue: e.target.value }, index)}
          />
          <MultiSelectWithSelectAll
            label="Possíveis cargos para o produto"
            value={plan.allowedRoles ?? []}
            options={roleOptions}
            onChange={(values) => handleChangePlans({ allowedRoles: values }, index)}
          />
          <ButtonDelete color="error" onClick={() => handleRemovePlan(index)} />
        </Row>
      ))}
      <Button onClick={handleAddPlan}>+ Adicionar plano</Button>
      <Row>
        <Subtitle>Tipo</Subtitle>
        <Subtitle style={{ marginLeft: "-30px" }}>Regra (idade maxima)</Subtitle>
        <Subtitle>Regra (escolaridade máxima)</Subtitle>
        <Subtitle>Documentos</Subtitle>
      </Row>
      {elegibilityRules.map((rule, index) => (
        <Row style={{ alignItems: "center"}}>
          <SelectForm
            label="Elegíveis ao produto"
            value={rule.elegible}
            onChange={(e) => handleChangeRules({ elegible: e.target.value as ElegibleType }, index)}
          >
            <MenuItem value={"DEPENDENTS"}>Dependentes</MenuItem>
          </SelectForm>
          <TextField
            label="Regra"
            value={rule.rule}
            onChange={(e) => handleChangeRules({ rule: e.target.value }, index)}
          />
          <TextField
            label="Segunda regra"
            value={rule.secondRule}
            onChange={(e) => handleChangeRules({ secondRule: e.target.value }, index)}
          />
          <MultiSelectWithSelectAll
            label="Documentos"
            value={rule.documents ?? []}
            options={documentOptions}
            onChange={(values) => handleChangeRules({ documents: values }, index)}
          />
          <ButtonDelete color="error" onClick={() => handleRemoveRule(index)} />
        </Row>
      ))}
      <Button onClick={handleAddRule}>+ Adicionar</Button>
    </Container>
  );
}
